import { useState, useRef, useEffect } from "react";
import { t, font, Button, Input } from "@realtyflows/ui";
import { getDealTypes, createDealType } from "../api";

const builtInGroups = [
  {
    label: "Residential",
    color: t.emerald,
    badge: "Core",
    badgeBg: `${t.emerald}15`,
    badgeColor: t.emerald,
    types: [
      { value: "Primary Residence Purchase", desc: "Buyer purchasing a home to live in", pipeline: "buyer" },
      { value: "Primary Residence Sale", desc: "Seller listing and selling their primary home", pipeline: "seller" },
      { value: "Condo / Townhouse", desc: "Includes HOA docs and condo rider" },
      { value: "New Construction", desc: "Buyer purchasing from a builder", pipeline: "buyer" },
    ],
  },
  {
    label: "Investment",
    color: t.amber,
    badge: "Expansion",
    badgeBg: `${t.amber}15`,
    badgeColor: t.amber,
    types: [
      { value: "Single-Family Rental", desc: "Purchase a home to rent out" },
      { value: "Multi-Family (2–4)", desc: "Small residential investment with rent roll" },
      { value: "Short-Term Rental", desc: "Airbnb / VRBO investor purchase" },
      { value: "Fix & Flip", desc: "Distressed property to renovate and resell" },
    ],
  },
  {
    label: "Commercial",
    color: t.textMuted,
    badge: "Specialized",
    badgeBg: t.surfaceWarm,
    badgeColor: t.textMuted,
    types: [
      { value: "Office / Retail", desc: "Commercial property sale" },
      { value: "Commercial Lease", desc: "NNN leases, CAM charges" },
      { value: "Multi-Family 5+", desc: "Apartment buildings — commercial financing" },
      { value: "Land / Lot", desc: "Raw land or lot for development" },
    ],
  },
];

export default function DealTypeSelect({ value, onChange, pipeline, label }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [customTypes, setCustomTypes] = useState([]);
  const [newType, setNewType] = useState({ name: "", description: "", category: "Custom", pipeline: "" });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !showCreateModal) {
        setOpen(false); setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCreateModal]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  // Load custom types when dropdown opens
  useEffect(() => {
    if (open) {
      getDealTypes().then(setCustomTypes).catch(() => {});
    }
  }, [open]);

  // Build the groups including custom types
  const customGroup = customTypes.length > 0 ? {
    label: "Custom",
    color: t.sky,
    badge: "Your Types",
    badgeBg: `${t.sky}15`,
    badgeColor: t.sky,
    types: customTypes.map((ct) => ({
      value: ct.name,
      desc: ct.description || "Custom deal type",
      pipeline: ct.pipeline || undefined,
    })),
  } : null;

  const allGroups = customGroup ? [...builtInGroups, customGroup] : builtInGroups;

  // Find selected type info
  let selectedGroup = null;
  for (const g of allGroups) {
    if (g.types.find((tp) => tp.value === value)) { selectedGroup = g; break; }
  }

  const q = search.toLowerCase();

  const handleCreateType = async () => {
    if (!newType.name.trim()) { setCreateError("Name is required"); return; }
    setCreating(true);
    setCreateError("");
    try {
      await createDealType(newType);
      const updated = await getDealTypes();
      setCustomTypes(updated);
      onChange(newType.name.trim());
      setNewType({ name: "", description: "", category: "Custom", pipeline: "" });
      setShowCreateModal(false);
      setOpen(false);
      setSearch("");
    } catch (err) {
      setCreateError(err.message || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {label && (
        <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>
          {label}
        </div>
      )}

      {/* Trigger */}
      <div
        onClick={() => { setOpen(!open); setSearch(""); }}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: t.surfaceWarm, border: `1.5px solid ${open ? t.emerald : t.border}`,
          borderRadius: 6, padding: "8px 10px", cursor: "pointer",
          transition: "border-color 0.15s, box-shadow 0.15s",
          boxShadow: open ? `0 0 0 3px ${t.emerald}18` : "none",
          minHeight: 36, boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden", flex: 1 }}>
          {selectedGroup && (
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: selectedGroup.color, flexShrink: 0 }} />
          )}
          <span style={{
            fontSize: 12, color: value ? t.textPrimary : t.textMuted, fontFamily: font.body, fontWeight: value ? 500 : 400,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {value || "Select deal type"}
          </span>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
          marginTop: 4, background: t.surface, border: `1.5px solid ${t.border}`,
          borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          maxHeight: 380, overflow: "hidden", display: "flex", flexDirection: "column",
          minWidth: 280,
        }}>
          {/* Search */}
          <div style={{ padding: "8px 10px", borderBottom: `1px solid ${t.border}` }}>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search deal types..."
              style={{
                width: "100%", boxSizing: "border-box", background: t.surfaceWarm,
                border: `1px solid ${t.border}`, borderRadius: 6, padding: "7px 10px",
                fontSize: 12, color: t.textPrimary, outline: "none", fontFamily: font.body,
              }}
            />
          </div>

          {/* Items */}
          <div style={{ overflowY: "auto", flex: 1, padding: "4px 0" }}>
            {(() => {
              let hasResults = false;
              const content = allGroups.map((group) => {
                const types = (pipeline
                  ? group.types.filter((tp) => !tp.pipeline || tp.pipeline === pipeline)
                  : group.types
                ).filter((tp) =>
                  !q || tp.value.toLowerCase().includes(q) || tp.desc.toLowerCase().includes(q)
                );
                if (types.length === 0) return null;
                hasResults = true;

                return (
                  <div key={group.label}>
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "8px 14px 4px", position: "sticky", top: 0, background: t.surface,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: group.color }} />
                        <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: font.mono }}>
                          {group.label}
                        </span>
                      </div>
                      <span style={{
                        fontSize: 9, fontWeight: 600, padding: "2px 7px", borderRadius: 10,
                        background: group.badgeBg, color: group.badgeColor, fontFamily: font.body,
                      }}>
                        {group.badge}
                      </span>
                    </div>
                    {types.map((tp) => {
                      const isSelected = value === tp.value;
                      return (
                        <div
                          key={tp.value}
                          onClick={() => { onChange(tp.value); setOpen(false); setSearch(""); }}
                          style={{
                            padding: "8px 14px 8px 28px", cursor: "pointer",
                            background: isSelected ? `${group.color}08` : "transparent",
                            borderLeft: isSelected ? `2.5px solid ${group.color}` : "2.5px solid transparent",
                            transition: "background 0.1s",
                          }}
                          onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = t.surfaceWarm; }}
                          onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                        >
                          <div style={{ fontSize: 12, fontWeight: isSelected ? 600 : 400, color: t.textPrimary, fontFamily: font.body, marginBottom: 1 }}>
                            {tp.value}
                          </div>
                          <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body, lineHeight: 1.4 }}>
                            {tp.desc}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              });

              if (!hasResults) {
                return (
                  <div style={{ padding: "20px 14px", textAlign: "center", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
                    No deal types match "{search}"
                  </div>
                );
              }
              return content;
            })()}

            {/* Add new deal type option */}
            {!q && (
              <div
                onClick={() => setShowCreateModal(true)}
                style={{
                  padding: "10px 14px", cursor: "pointer", borderTop: `1px solid ${t.border}`,
                  display: "flex", alignItems: "center", gap: 8, marginTop: 4,
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = t.surfaceWarm; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{
                  width: 20, height: 20, borderRadius: 6, background: `${t.emerald}15`,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.emerald} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: t.emerald, fontFamily: font.body }}>
                  Add new deal type
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Deal Type Modal */}
      {showCreateModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 999,
          display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={() => { setShowCreateModal(false); setCreateError(""); }}>
          <div style={{
            background: t.surface, borderRadius: 12, padding: 24, width: 400,
            border: `1px solid ${t.emerald}30`, boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 4, fontFamily: font.display }}>
              New Deal Type
            </div>
            <div style={{ fontSize: 12, color: t.textSecondary, marginBottom: 20, fontFamily: font.body }}>
              Create a custom deal type for your transactions
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Input
                label="Name"
                value={newType.name}
                onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                placeholder="e.g., 1031 Exchange"
              />
              <Input
                label="Description (optional)"
                value={newType.description}
                onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                placeholder="Brief description of this deal type"
              />
              <div>
                <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Category</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {["Custom", "Residential", "Investment", "Commercial"].map((cat) => (
                    <div key={cat} onClick={() => setNewType({ ...newType, category: cat })}
                      style={{
                        padding: "6px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                        background: newType.category === cat ? `${t.emerald}15` : t.surfaceWarm,
                        border: `1px solid ${newType.category === cat ? t.emerald : t.border}`,
                        color: newType.category === cat ? t.emerald : t.textSecondary,
                        fontFamily: font.body, fontWeight: 600,
                      }}>
                      {cat}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>Pipeline (optional)</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[{ key: "", label: "Any" }, { key: "buyer", label: "Buyer" }, { key: "seller", label: "Seller" }].map((p) => (
                    <div key={p.key} onClick={() => setNewType({ ...newType, pipeline: p.key })}
                      style={{
                        padding: "6px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                        background: newType.pipeline === p.key ? `${t.emerald}15` : t.surfaceWarm,
                        border: `1px solid ${newType.pipeline === p.key ? t.emerald : t.border}`,
                        color: newType.pipeline === p.key ? t.emerald : t.textSecondary,
                        fontFamily: font.body, fontWeight: 600,
                      }}>
                      {p.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {createError && (
              <div style={{ marginTop: 12, fontSize: 11, color: t.coral, fontFamily: font.body }}>
                {createError}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <Button variant="outline" size="sm" onClick={() => { setShowCreateModal(false); setCreateError(""); }}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleCreateType} disabled={creating}>
                {creating ? "Creating..." : "Create Deal Type"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
